import React from 'react';
import apiClient from '../../Api/ApiClient';
import { 
  Sparkles, CheckCircle2, Clock, Search, RefreshCw, FileText, 
  Code2, ExternalLink, Download, Copy, Check, Eye, AlertCircle, 
  Layers, Database, FileCode, CheckCheck, Loader2, ArrowRight, X,
  Zap, Play, Square, CheckSquare, StopCircle, ListChecks
} from 'lucide-react';

interface AdminOcrTabProps {
  openPdfPreview: (doc: any) => void;
  categoryOptions: Array<{ value: string; label: string }>;
}

export const AdminOcrTab: React.FC<AdminOcrTabProps> = ({ openPdfPreview, categoryOptions }) => {
  const [stats, setStats] = React.useState<{
    totalDocuments: number;
    processedCount: number;
    unprocessedCount: number;
    coveragePercentage: number;
    ocrBucket: string;
  } | null>(null);
  const [statsLoading, setStatsLoading] = React.useState(false);

  const [documents, setDocuments] = React.useState<any[]>([]);
  const [loadingDocs, setLoadingDocs] = React.useState(false);
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(20);
  const [totalCount, setTotalCount] = React.useState(0);

  // Filtres
  const [ocrStatusFilter, setOcrStatusFilter] = React.useState<'ALL' | 'NOT_PROCESSED' | 'PROCESSED'>('ALL');
  const [categoryFilter, setCategoryFilter] = React.useState<string>('');
  const [searchInput, setSearchInput] = React.useState<string>('');
  const [searchTerm, setSearchTerm] = React.useState<string>('');

  // Sélection multiple par lot
  const [selectedDocIds, setSelectedDocIds] = React.useState<Set<string>>(new Set());

  // Traitement en cours (unitaire & par lot en tâche de fond)
  const [processingIds, setProcessingIds] = React.useState<Record<string, boolean>>({});
  const [alertMessage, setAlertMessage] = React.useState<{ type: 'success' | 'error'; message: string } | null>(null);
  const [batchJob, setBatchJob] = React.useState<any | null>(null);
  const [batchLaunching, setBatchLaunching] = React.useState<boolean>(false);

  // Modale d'inspection du JSON
  const [inspectDoc, setInspectDoc] = React.useState<any | null>(null);
  const [inspectJsonData, setInspectJsonData] = React.useState<any | null>(null);
  const [inspectLoading, setInspectLoading] = React.useState<boolean>(false);
  const [inspectError, setInspectError] = React.useState<string | null>(null);
  const [inspectMode, setInspectMode] = React.useState<'STRUCTURE' | 'RAW'>('STRUCTURE');
  const [copiedJson, setCopiedJson] = React.useState(false);

  const fetchStats = React.useCallback(async () => {
    setStatsLoading(true);
    try {
      const res = await apiClient.get<any>('/api/admin/ocr/stats');
      if (res) {
        setStats(res);
      }
    } catch (err) {
      console.warn("Impossible de récupérer les statistiques OCR:", err);
    } finally {
      setStatsLoading(false);
    }
  }, []);

  const fetchDocuments = React.useCallback(async () => {
    setLoadingDocs(true);
    try {
      const params = new URLSearchParams();
      params.set('page', page.toString());
      params.set('pageSize', pageSize.toString());
      if (ocrStatusFilter !== 'ALL') params.set('ocrStatus', ocrStatusFilter);
      if (categoryFilter) params.set('categoryName', categoryFilter);
      if (searchTerm.trim()) params.set('search', searchTerm.trim());

      const res = await apiClient.get<any>(`/api/admin/ocr/documents?${params.toString()}`);
      if (res) {
        setDocuments(res.items || []);
        setTotalCount(res.totalCount || 0);
      }
    } catch (err) {
      console.error("Erreur lors de la récupération des documents OCR:", err);
    } finally {
      setLoadingDocs(false);
    }
  }, [page, pageSize, ocrStatusFilter, categoryFilter, searchTerm]);

  React.useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  React.useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    setSearchTerm(searchInput);
  };

  const handleLaunchOcr = async (doc: any) => {
    const docId = doc.id;
    setProcessingIds(prev => ({ ...prev, [docId]: true }));
    setAlertMessage(null);

    try {
      const res = await apiClient.post<any>(`/api/admin/ocr/process/${docId}?force=true`, {});
      const chunksCount = res.chunksIndexed ?? res.payload?.stats?.articleCount ?? 0;
      const wordsCount = res.stats?.totalWords ?? res.payload?.stats?.totalWords ?? 0;
      setAlertMessage({
        type: 'success',
        message: `Traitement OCR réussi pour "${doc.referenceNumber || doc.officialTitle}" ! Index Elasticsearch mis à jour avec succès (${chunksCount} chunks indexés, ${wordsCount} mots traités).`
      });

      // Actualiser immédiatement la liste et les compteurs
      fetchStats();
      fetchDocuments();
    } catch (err: any) {
      console.error("Erreur lancement OCR:", err);
      setAlertMessage({
        type: 'error',
        message: err.message || err.data?.message || "Erreur lors du traitement OCR. Assurez-vous que le service OCR Python est en cours d'exécution."
      });
    } finally {
      setProcessingIds(prev => ({ ...prev, [docId]: false }));
    }
  };

  const toggleSelectDoc = (id: string) => {
    setSelectedDocIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAllVisible = () => {
    if (documents.length === 0) return;
    const allVisible = documents.every(d => selectedDocIds.has(d.id));
    if (allVisible) {
      setSelectedDocIds(prev => {
        const next = new Set(prev);
        documents.forEach(d => next.delete(d.id));
        return next;
      });
    } else {
      setSelectedDocIds(prev => {
        const next = new Set(prev);
        documents.forEach(d => next.add(d.id));
        return next;
      });
    }
  };

  const deselectAll = () => setSelectedDocIds(new Set());

  // Polling du statut du lot en cours d'exécution
  const pollBatchStatus = React.useCallback(async (jobId?: string) => {
    try {
      const res = await apiClient.get<any>(`/api/admin/ocr/batch/status${jobId ? `?jobId=${jobId}` : ''}`);
      if (res && res.hasActiveJob) {
        setBatchJob(res);
        if (!res.isRunning) {
          fetchStats();
          fetchDocuments();
        }
      } else if (res && res.total > 0 && !res.isRunning) {
        setBatchJob(res);
      } else {
        setBatchJob((prev: any) => (prev?.isRunning ? { ...prev, isRunning: false } : null));
      }
    } catch {
      // Ignorer
    }
  }, [fetchStats, fetchDocuments]);

  // Polling régulier si un lot est en cours
  React.useEffect(() => {
    pollBatchStatus();
    const interval = setInterval(() => {
      if (batchJob?.isRunning) {
        pollBatchStatus(batchJob.jobId);
      }
    }, 2500);
    return () => clearInterval(interval);
  }, [batchJob?.isRunning, pollBatchStatus]);

  // Lancer le lot pour les documents sélectionnés
  const handleLaunchBatchSelected = async (force: boolean = true) => {
    if (selectedDocIds.size === 0) return;
    setBatchLaunching(true);
    setAlertMessage(null);
    try {
      const ids = Array.from(selectedDocIds);
      const res = await apiClient.post<any>('/api/admin/ocr/batch', {
        documentIds: ids,
        force
      });
      setBatchJob({
        jobId: res.jobId,
        total: res.total,
        processed: 0,
        successCount: 0,
        failedCount: 0,
        isRunning: true,
        progressPercentage: 0,
        startedAt: new Date().toISOString()
      });
      setSelectedDocIds(new Set());
      setAlertMessage({
        type: 'success',
        message: `Lot OCR lancé avec succès pour ${res.total} document(s). Suivez la progression ci-dessous.`
      });
    } catch (err: any) {
      setAlertMessage({
        type: 'error',
        message: err.message || err.data?.message || "Erreur lors du lancement du lot OCR."
      });
    } finally {
      setBatchLaunching(false);
    }
  };

  // Lancer le lot pour tous les documents non traités ou tout le corpus
  const handleLaunchBatchAll = async (onlyUnprocessed: boolean = true) => {
    const count = onlyUnprocessed ? (stats?.unprocessedCount ?? 0) : (stats?.totalDocuments ?? 0);
    const label = onlyUnprocessed ? "tous les documents non encore traités" : "la totalité du corpus juridique";
    if (!window.confirm(`Voulez-vous lancer la régénération OCR pour ${label} (~${count} actes) ? Cette tâche s'exécutera en arrière-plan sans bloquer votre interface.`)) {
      return;
    }

    setBatchLaunching(true);
    setAlertMessage(null);
    try {
      const res = await apiClient.post<any>(`/api/admin/ocr/batch-all?onlyUnprocessed=${onlyUnprocessed}&force=true`, {});
      setBatchJob({
        jobId: res.jobId,
        total: res.total,
        processed: 0,
        successCount: 0,
        failedCount: 0,
        isRunning: true,
        progressPercentage: 0,
        startedAt: new Date().toISOString()
      });
      setAlertMessage({
        type: 'success',
        message: `${res.message} Suivez la progression ci-dessous.`
      });
    } catch (err: any) {
      setAlertMessage({
        type: 'error',
        message: err.message || err.data?.message || "Erreur lors du lancement du traitement massif."
      });
    } finally {
      setBatchLaunching(false);
    }
  };

  // Annuler le lot en cours
  const handleCancelBatch = async () => {
    if (!batchJob?.jobId) return;
    try {
      await apiClient.post<any>(`/api/admin/ocr/batch/cancel/${batchJob.jobId}`, {});
      setBatchJob((prev: any) => prev ? { ...prev, isRunning: false, isCancelled: true } : null);
      fetchStats();
      fetchDocuments();
    } catch (err) {
      console.warn("Erreur annulation lot:", err);
    }
  };

  const handleOpenJsonModal = async (doc: any) => {
    setInspectDoc(doc);
    setInspectJsonData(null);
    setInspectError(null);
    setInspectLoading(true);
    setInspectMode('STRUCTURE');
    setCopiedJson(false);

    try {
      const data = await apiClient.get<any>(`/api/admin/ocr/json/${doc.id}`);
      setInspectJsonData(data);
    } catch (err: any) {
      console.error("Erreur chargement JSON OCR:", err);
      setInspectError(err.message || err.data?.message || "Impossible de charger le fichier JSON depuis MinIO.");
    } finally {
      setInspectLoading(false);
    }
  };

  const handleCopyJson = () => {
    if (!inspectJsonData) return;
    navigator.clipboard.writeText(JSON.stringify(inspectJsonData, null, 2));
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 3000);
  };

  const handleDownloadJson = () => {
    if (!inspectJsonData || !inspectDoc) return;
    const jsonStr = JSON.stringify(inspectJsonData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${(inspectDoc.referenceNumber || 'ocr_document').replace(/[/\\?%*:|"<>]/g, '_')}.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-5">
      {/* ── KPI & Stats Header ────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Documents */}
        <div className="bg-white border border-zinc-200/80 rounded-2xl p-4.5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Actes Répertoriés</span>
            <div className="p-2 bg-primary-50 text-primary-700 rounded-xl">
              <Database className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-display font-black text-zinc-900">
            {stats?.totalDocuments ?? '—'}
          </div>
          <div className="text-[11px] text-zinc-400 mt-1">Actes avec PDF source dans MinIO</div>
        </div>

        {/* Traités en OCR */}
        <div className="bg-gradient-to-br from-emerald-50/70 to-white border border-emerald-200/80 rounded-2xl p-4.5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-emerald-800 uppercase tracking-wider">Traités en OCR</span>
            <div className="p-2 bg-emerald-100 text-emerald-700 rounded-xl">
              <CheckCircle2 className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-display font-black text-emerald-950 flex items-baseline gap-2">
            {stats?.processedCount ?? '—'}
            {stats && (
              <span className="text-xs font-bold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
                {stats.coveragePercentage}%
              </span>
            )}
          </div>
          <div className="text-[11px] text-emerald-600/80 mt-1">JSON structuré déposé dans MinIO</div>
        </div>

        {/* Non traités / En attente */}
        <div className="bg-gradient-to-br from-amber-50/70 to-white border border-amber-200/80 rounded-2xl p-4.5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-amber-800 uppercase tracking-wider">En attente d'OCR</span>
            <div className="p-2 bg-amber-100 text-amber-700 rounded-xl">
              <Clock className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-display font-black text-amber-950">
            {stats?.unprocessedCount ?? '—'}
          </div>
          <div className="text-[11px] text-amber-600/80 mt-1">Actes prêts pour la numérisation</div>
        </div>

        {/* Progression Globale */}
        <div className="bg-white border border-zinc-200/80 rounded-2xl p-4.5 shadow-sm hover:shadow transition-shadow">
          <div className="flex items-center justify-between mb-2">
            <span className="text-xs font-semibold text-zinc-500 uppercase tracking-wider">Couverture OCR</span>
            <div className="p-2 bg-purple-50 text-purple-700 rounded-xl">
              <Sparkles className="w-4 h-4" />
            </div>
          </div>
          <div className="text-2xl font-display font-black text-zinc-900">
            {stats?.coveragePercentage ?? 0}%
          </div>
          <div className="w-full bg-zinc-100 h-2 rounded-full overflow-hidden mt-2">
            <div 
              className="bg-gradient-to-r from-emerald-500 to-teal-500 h-full rounded-full transition-all duration-500"
              style={{ width: `${Math.min(100, stats?.coveragePercentage || 0)}%` }}
            ></div>
          </div>
        </div>
      </div>

      {/* ── Toast Alert ──────────────────────────────────────────────── */}
      {alertMessage && (
        <div className={`p-4 rounded-2xl border flex items-start justify-between gap-3 shadow-md animate-fade-in ${
          alertMessage.type === 'success' 
            ? 'bg-emerald-50/90 border-emerald-200 text-emerald-900' 
            : 'bg-red-50/90 border-red-200 text-red-900'
        }`}>
          <div className="flex items-start gap-3">
            {alertMessage.type === 'success' ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            ) : (
              <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            )}
            <div className="text-sm font-medium">{alertMessage.message}</div>
          </div>
          <button 
            onClick={() => setAlertMessage(null)}
            className="text-zinc-400 hover:text-zinc-600 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ── Active Batch Job Monitor ───────────────────────────────────── */}
      {batchJob && (batchJob.isRunning || batchJob.total > 0) && (
        <div className={`p-4.5 rounded-2xl border shadow-lg transition-all animate-fade-in ${
          batchJob.isRunning 
            ? 'bg-gradient-to-r from-zinc-900 via-primary-950 to-zinc-900 border-primary-500/40 text-white' 
            : batchJob.failedCount > 0 && batchJob.successCount === 0
            ? 'bg-red-50 border-red-200 text-red-950'
            : 'bg-emerald-50 border-emerald-200 text-emerald-950'
        }`}>
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-3">
            <div className="flex items-center gap-3">
              <div className={`p-2.5 rounded-xl ${batchJob.isRunning ? 'bg-primary-900/80 text-primary-300 ring-1 ring-primary-500/50' : 'bg-white shadow-xs'}`}>
                {batchJob.isRunning ? (
                  <Sparkles className="w-5 h-5 text-amber-300 animate-spin" />
                ) : (
                  <CheckCheck className="w-5 h-5 text-emerald-600" />
                )}
              </div>
              <div>
                <div className="font-bold text-sm flex items-center gap-2">
                  <span>{batchJob.isRunning ? "Régénération OCR en arrière-plan (sans blocage)..." : "Traitement OCR par lot terminé"}</span>
                  <span className={`text-[10px] font-mono px-2 py-0.5 rounded-full font-extrabold ${
                    batchJob.isRunning ? 'bg-amber-400 text-zinc-950 shadow-xs' : 'bg-emerald-200 text-emerald-900'
                  }`}>
                    {batchJob.progressPercentage}%
                  </span>
                </div>
                <div className={`text-xs ${batchJob.isRunning ? 'text-primary-200' : 'text-zinc-600'} mt-0.5 font-medium`}>
                  {batchJob.currentDocumentTitle 
                    ? `Document en cours : ${batchJob.currentDocumentTitle}` 
                    : `${batchJob.successCount} réussis, ${batchJob.failedCount} échecs sur ${batchJob.total} actes`}
                </div>
              </div>
            </div>

            <div className="flex items-center gap-2 self-end sm:self-center">
              {batchJob.isRunning ? (
                <button
                  type="button"
                  onClick={handleCancelBatch}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition shadow cursor-pointer"
                >
                  <StopCircle className="w-3.5 h-3.5" />
                  Arrêter le lot
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setBatchJob(null)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 bg-zinc-200 hover:bg-zinc-300 text-zinc-800 rounded-xl text-xs font-bold transition cursor-pointer"
                >
                  Fermer
                </button>
              )}
            </div>
          </div>

          {/* Progress bar */}
          <div className="w-full bg-black/30 h-2.5 rounded-full overflow-hidden mb-2 p-0.5 border border-white/10">
            <div 
              className={`h-full rounded-full transition-all duration-300 ${batchJob.isRunning ? 'bg-gradient-to-r from-amber-400 to-emerald-400' : 'bg-emerald-500'}`}
              style={{ width: `${Math.min(100, Math.max(3, batchJob.progressPercentage))}%` }}
            />
          </div>

          <div className="flex items-center justify-between text-[11px] font-mono opacity-85">
            <span>Progression : {batchJob.processed} / {batchJob.total} actes traités</span>
            <span>Succès: {batchJob.successCount} • Échecs: {batchJob.failedCount}</span>
          </div>
        </div>
      )}

      {/* ── Main Panel ────────────────────────────────────────────────── */}
      <div className="bg-white border border-zinc-200/80 rounded-3xl overflow-hidden shadow-xl shadow-zinc-900/5">
        
        {/* Toolbar & Filters */}
        <div className="p-5 bg-gradient-to-r from-zinc-50 to-white border-b border-zinc-100 space-y-4">
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div>
              <h3 className="font-display text-lg font-bold text-zinc-900 flex items-center gap-2.5">
                <Sparkles className="w-5 h-5 text-primary-600" />
                Numérisation OCR &amp; Structuration Juridique
              </h3>
              <p className="text-xs text-zinc-500 font-medium mt-0.5">
                Génération des boîtes englobantes mot par mot, détection des articles et publication miroir JSON dans MinIO.
              </p>
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              {/* Bouton Traiter la sélection */}
              {selectedDocIds.size > 0 && (
                <div className="inline-flex items-center gap-1.5 p-1 bg-primary-50 rounded-xl border border-primary-200 animate-fade-in">
                  <button
                    type="button"
                    onClick={() => handleLaunchBatchSelected(true)}
                    disabled={batchLaunching || (batchJob?.isRunning ?? false)}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-gradient-to-r from-primary-600 to-primary-700 hover:from-primary-700 hover:to-primary-800 text-white rounded-lg text-xs font-bold shadow-sm transition-all cursor-pointer disabled:opacity-50"
                  >
                    <Zap className="w-3.5 h-3.5 text-amber-300" />
                    Régénérer la sélection ({selectedDocIds.size})
                  </button>
                  <button
                    type="button"
                    onClick={deselectAll}
                    className="p-1.5 text-zinc-400 hover:text-zinc-600 rounded-lg hover:bg-zinc-200/60 transition-colors"
                    title="Désélectionner tout"
                  >
                    <X className="w-3.5 h-3.5" />
                  </button>
                </div>
              )}

              {/* Bouton Traiter tous les non traités */}
              <button
                type="button"
                onClick={() => handleLaunchBatchAll(true)}
                disabled={batchLaunching || (batchJob?.isRunning ?? false) || (stats?.unprocessedCount ?? 0) === 0}
                className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-600 hover:to-amber-700 text-white rounded-xl text-xs font-bold shadow-sm transition-all cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                title="Lancer l'OCR pour tous les documents qui n'ont pas encore de fichier JSON MinIO"
              >
                <Sparkles className="w-3.5 h-3.5" />
                Tout traiter ({stats?.unprocessedCount ?? 0} non traités)
              </button>

              <button
                type="button"
                onClick={() => { fetchStats(); fetchDocuments(); }}
                disabled={loadingDocs || statsLoading}
                className="inline-flex items-center gap-1.5 px-3 py-2 bg-white hover:bg-zinc-50 border border-zinc-200 rounded-xl text-xs font-bold text-zinc-700 shadow-sm transition-all cursor-pointer"
                title="Actualiser la liste"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${(loadingDocs || statsLoading) ? 'animate-spin' : ''}`} />
                Actualiser
              </button>
            </div>
          </div>

          {/* Quick Status Filter Tabs + Search + Category */}
          <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3 pt-2 border-t border-zinc-100">
            {/* Status Pills */}
            <div className="inline-flex p-1 bg-zinc-100 rounded-xl border border-zinc-200/60 self-start">
              <button
                type="button"
                onClick={() => { setOcrStatusFilter('ALL'); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  ocrStatusFilter === 'ALL'
                    ? 'bg-white text-zinc-900 shadow-sm'
                    : 'text-zinc-600 hover:text-zinc-900'
                }`}
              >
                Tous ({stats?.totalDocuments ?? 0})
              </button>

              <button
                type="button"
                onClick={() => { setOcrStatusFilter('NOT_PROCESSED'); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                  ocrStatusFilter === 'NOT_PROCESSED'
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'text-amber-700 hover:text-amber-900 hover:bg-amber-50/50'
                }`}
              >
                <Clock className="w-3.5 h-3.5" />
                Non traités ({stats?.unprocessedCount ?? 0})
              </button>

              <button
                type="button"
                onClick={() => { setOcrStatusFilter('PROCESSED'); setPage(1); }}
                className={`px-3 py-1.5 rounded-lg text-xs font-bold flex items-center gap-1.5 transition-all ${
                  ocrStatusFilter === 'PROCESSED'
                    ? 'bg-emerald-600 text-white shadow-sm'
                    : 'text-emerald-700 hover:text-emerald-900 hover:bg-emerald-50/50'
                }`}
              >
                <CheckCircle2 className="w-3.5 h-3.5" />
                Déjà traités ({stats?.processedCount ?? 0})
              </button>
            </div>

            {/* Category Filter & Search Bar */}
            <div className="flex items-center gap-2.5 flex-1 lg:max-w-xl">
              <select
                value={categoryFilter}
                onChange={(e) => { setCategoryFilter(e.target.value); setPage(1); }}
                className="px-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs font-medium text-zinc-700 focus:outline-none focus:border-primary-500"
              >
                <option value="">Toutes les natures</option>
                {categoryOptions.map(opt => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>

              <form onSubmit={handleSearchSubmit} className="flex-1 relative">
                <Search className="w-4 h-4 text-zinc-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                  type="text"
                  placeholder="Rechercher par référence, titre..."
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className="w-full pl-9 pr-3 py-2 bg-white border border-zinc-200 rounded-xl text-xs placeholder-zinc-400 focus:outline-none focus:border-primary-500"
                />
              </form>
            </div>
          </div>
        </div>

        {/* ── Table of Documents ────────────────────────────────────────── */}
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-50/80 border-b border-zinc-200/80 text-[11px] font-bold uppercase tracking-wider text-zinc-500">
                <th className="py-3.5 px-3 text-center w-10">
                  <input
                    type="checkbox"
                    checked={documents.length > 0 && documents.every(d => selectedDocIds.has(d.id))}
                    onChange={toggleSelectAllVisible}
                    className="rounded border-zinc-300 text-primary-600 focus:ring-primary-500 cursor-pointer w-4 h-4"
                    title="Tout sélectionner sur cette page"
                  />
                </th>
                <th className="py-3.5 px-4">Référence &amp; Titre</th>
                <th className="py-3.5 px-4">Nature &amp; Année</th>
                <th className="py-3.5 px-4 text-center">Pages</th>
                <th className="py-3.5 px-4">Statut OCR</th>
                <th className="py-3.5 px-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100 text-xs">
              {loadingDocs && documents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-zinc-500">
                    <Loader2 className="w-6 h-6 animate-spin mx-auto text-primary-600 mb-2" />
                    Chargement des documents...
                  </td>
                </tr>
              ) : documents.length === 0 ? (
                <tr>
                  <td colSpan={6} className="py-12 text-center text-zinc-500">
                    <Database className="w-8 h-8 mx-auto text-zinc-300 mb-2" />
                    Aucun document trouvé pour les filtres sélectionnés.
                  </td>
                </tr>
              ) : (
                documents.map((doc: any) => {
                  const isProcessing = !!processingIds[doc.id];
                  const isSelected = selectedDocIds.has(doc.id);
                  return (
                    <tr key={doc.id} className={`transition-colors ${isSelected ? 'bg-primary-50/50 hover:bg-primary-50/80' : 'hover:bg-zinc-50/60'}`}>
                      {/* Checkbox de sélection */}
                      <td className="py-3.5 px-3 text-center w-10">
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => toggleSelectDoc(doc.id)}
                          className="rounded border-zinc-300 text-primary-600 focus:ring-primary-500 cursor-pointer w-4 h-4"
                        />
                      </td>

                      {/* Référence & Titre */}
                      <td className="py-3.5 px-4 max-w-md">
                        <div className="font-bold text-zinc-900 flex items-center gap-1.5">
                          <span>{doc.referenceNumber || 'Sans référence'}</span>
                        </div>
                        <div className="text-[11px] text-zinc-500 line-clamp-2 mt-0.5 font-normal leading-relaxed">
                          {doc.officialTitle || 'Titre non spécifié'}
                        </div>
                      </td>

                      {/* Nature & Année */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-semibold bg-zinc-100 text-zinc-800 border border-zinc-200">
                          {doc.categoryName || 'Acte'}
                        </span>
                        <div className="text-[11px] text-zinc-400 mt-1">
                          Année : {doc.effectiveYear || '—'}
                        </div>
                      </td>

                      {/* Pages */}
                      <td className="py-3.5 px-4 text-center whitespace-nowrap font-medium text-zinc-600">
                        {doc.pageCount ? `${doc.pageCount} p.` : '—'}
                      </td>

                      {/* Statut OCR */}
                      <td className="py-3.5 px-4 whitespace-nowrap">
                        {doc.isOcrProcessed ? (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-emerald-100 text-emerald-800 border border-emerald-200">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
                            Traité (JSON prêt)
                          </div>
                        ) : (
                          <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[11px] font-bold bg-amber-100 text-amber-800 border border-amber-200">
                            <Clock className="w-3.5 h-3.5 text-amber-600" />
                            Non traité
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3.5 px-4 text-right whitespace-nowrap">
                        <div className="inline-flex items-center gap-1.5">
                          {/* Visualiser le PDF original */}
                          <button
                            type="button"
                            onClick={() => openPdfPreview(doc)}
                            className="p-1.5 text-zinc-500 hover:text-zinc-800 hover:bg-zinc-100 rounded-lg transition-colors border border-transparent hover:border-zinc-200"
                            title="Voir le fichier PDF source"
                          >
                            <Eye className="w-4 h-4" />
                          </button>

                          {/* Visualiser le JSON structuré si traité */}
                          {doc.isOcrProcessed && (
                            <button
                              type="button"
                              onClick={() => handleOpenJsonModal(doc)}
                              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-bold text-primary-700 bg-primary-50 hover:bg-primary-100 border border-primary-200 rounded-lg transition-colors cursor-pointer shadow-sm"
                              title="Visualiser le fichier .json MinIO"
                            >
                              <FileCode className="w-3.5 h-3.5" />
                              Voir JSON
                            </button>
                          )}

                          {/* Bouton Lancer / Relancer OCR */}
                          <button
                            type="button"
                            disabled={isProcessing}
                            onClick={() => handleLaunchOcr(doc)}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all shadow-sm ${
                              isProcessing
                                ? 'bg-zinc-100 text-zinc-400 cursor-not-allowed'
                                : doc.isOcrProcessed
                                ? 'bg-zinc-100 hover:bg-zinc-200 text-zinc-700 border border-zinc-200'
                                : 'bg-primary-600 hover:bg-primary-700 text-white shadow-primary-600/20'
                            }`}
                            title={doc.isOcrProcessed ? "Relancer l'extraction OCR haute précision" : "Lancer l'extraction OCR et générer le JSON MinIO"}
                          >
                            {isProcessing ? (
                              <>
                                <Loader2 className="w-3.5 h-3.5 animate-spin text-primary-600" />
                                Extraction...
                              </>
                            ) : (
                              <>
                                <Sparkles className="w-3.5 h-3.5" />
                                {doc.isOcrProcessed ? 'Relancer' : 'Traiter OCR'}
                              </>
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="p-4 bg-zinc-50/80 border-t border-zinc-200/80 flex items-center justify-between text-xs text-zinc-500">
          <div>
            Total : <strong>{totalCount}</strong> document{totalCount > 1 ? 's' : ''}
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              disabled={page <= 1}
              onClick={() => setPage(p => Math.max(1, p - 1))}
              className="px-3 py-1.5 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Précédent
            </button>
            <span className="font-semibold text-zinc-800">
              Page {page} / {Math.ceil(totalCount / pageSize) || 1}
            </span>
            <button
              type="button"
              disabled={page >= Math.ceil(totalCount / pageSize)}
              onClick={() => setPage(p => p + 1)}
              className="px-3 py-1.5 bg-white border border-zinc-200 rounded-lg hover:bg-zinc-50 disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Suivant
            </button>
          </div>
        </div>
      </div>

      {/* ── Modal Visualiseur JSON Haute Précision ─────────────────────── */}
      {inspectDoc && (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-white rounded-3xl max-w-4xl w-full max-h-[90vh] flex flex-col shadow-2xl overflow-hidden border border-zinc-200">
            {/* Modal Header */}
            <div className="p-5 bg-gradient-to-r from-zinc-900 to-zinc-800 text-white flex items-center justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-center gap-2 text-xs font-bold text-emerald-400 uppercase tracking-widest">
                  <FileCode className="w-4 h-4" />
                  Visualiseur OCR JSON MinIO (Bucket: documents-ocr-json)
                </div>
                <h3 className="font-display font-bold text-lg text-white truncate mt-0.5">
                  {inspectDoc.referenceNumber || inspectDoc.officialTitle}
                </h3>
                <div className="text-xs text-zinc-400 truncate">
                  Clé MinIO : {inspectDoc.ocrJsonKey || '—'}
                </div>
              </div>

              <div className="flex items-center gap-2 shrink-0">
                {inspectJsonData && (
                  <>
                    <button
                      type="button"
                      onClick={handleCopyJson}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white/10 hover:bg-white/20 rounded-xl text-xs font-semibold text-white transition-colors"
                      title="Copier le JSON complet"
                    >
                      {copiedJson ? <CheckCheck className="w-4 h-4 text-emerald-400" /> : <Copy className="w-4 h-4" />}
                      {copiedJson ? 'Copié !' : 'Copier'}
                    </button>

                    <button
                      type="button"
                      onClick={handleDownloadJson}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-primary-600 hover:bg-primary-700 rounded-xl text-xs font-bold text-white transition-colors shadow-sm"
                      title="Télécharger le fichier .json"
                    >
                      <Download className="w-4 h-4" />
                      Télécharger .json
                    </button>
                  </>
                )}

                <button
                  type="button"
                  onClick={() => setInspectDoc(null)}
                  className="p-1.5 text-zinc-400 hover:text-white rounded-xl hover:bg-white/10 transition-colors ml-2"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            {/* Modal Subheader: Mode selector & metadata chips */}
            {inspectJsonData && (
              <div className="px-5 py-3 bg-zinc-50 border-b border-zinc-200 flex flex-wrap items-center justify-between gap-3 text-xs">
                {/* Tabs Switcher */}
                <div className="inline-flex p-1 bg-zinc-200/80 rounded-xl">
                  <button
                    type="button"
                    onClick={() => setInspectMode('STRUCTURE')}
                    className={`px-3 py-1 rounded-lg font-bold transition-all ${
                      inspectMode === 'STRUCTURE'
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    Articles &amp; Structure ({inspectJsonData.stats?.articleCount ?? inspectJsonData.segmentation?.articles?.length ?? 0})
                  </button>

                  <button
                    type="button"
                    onClick={() => setInspectMode('RAW')}
                    className={`px-3 py-1 rounded-lg font-bold transition-all ${
                      inspectMode === 'RAW'
                        ? 'bg-white text-zinc-900 shadow-sm'
                        : 'text-zinc-600 hover:text-zinc-900'
                    }`}
                  >
                    Code JSON Brut
                  </button>
                </div>

                {/* Quick Stats Badges */}
                <div className="flex items-center gap-2 flex-wrap text-[11px] font-semibold text-zinc-600">
                  <span className="bg-zinc-200/80 px-2 py-0.5 rounded-md">
                    Pages : {inspectJsonData.stats?.totalPages || inspectJsonData.pages?.length || '—'}
                  </span>
                  <span className="bg-zinc-200/80 px-2 py-0.5 rounded-md">
                    Mots : {inspectJsonData.stats?.totalWords || '—'}
                  </span>
                  <span className="bg-emerald-100 text-emerald-800 px-2 py-0.5 rounded-md">
                    Confiance OCR : {inspectJsonData.stats?.avgOcrConfidence ? `${inspectJsonData.stats.avgOcrConfidence}%` : 'N/A'}
                  </span>
                  <span className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded-md">
                    Moteur : {inspectJsonData.engine?.native} + {inspectJsonData.engine?.ocr}
                  </span>
                </div>
              </div>
            )}

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto flex-1 text-sm bg-white">
              {inspectLoading ? (
                <div className="py-20 text-center text-zinc-500">
                  <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary-600 mb-2" />
                  Récupération du fichier JSON depuis MinIO...
                </div>
              ) : inspectError ? (
                <div className="p-4 bg-red-50 border border-red-200 rounded-2xl text-red-900 text-xs">
                  <strong>Erreur :</strong> {inspectError}
                </div>
              ) : !inspectJsonData ? (
                <div className="py-20 text-center text-zinc-400">Aucune donnée disponible.</div>
              ) : inspectMode === 'STRUCTURE' ? (
                /* Structured Legal Articles View */
                <div className="space-y-4">
                  {inspectJsonData.segmentation?.articles && inspectJsonData.segmentation.articles.length > 0 ? (
                    inspectJsonData.segmentation.articles.map((art: any, idx: number) => (
                      <div 
                        key={idx} 
                        className="p-4 bg-zinc-50 border border-zinc-200/80 rounded-2xl hover:border-primary-300 transition-colors shadow-sm"
                      >
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className={`w-2.5 h-2.5 rounded-full ${art.type === 'preamble' ? 'bg-amber-500' : 'bg-primary-600'}`}></span>
                            <span className="font-display font-bold text-zinc-900 text-sm">
                              {art.label || art.title || (art.articleNumber ? `Article ${art.articleNumber}` : `Élément #${idx + 1}`)}
                            </span>
                            {art.type === 'preamble' && (
                              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                                Visas &amp; Préambule
                              </span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-zinc-500 bg-white border border-zinc-200 px-2 py-0.5 rounded-md">
                              {art.pages && art.pages.length > 0 ? `Page ${art.pages.join(', ')}` : `Page ${art.startPage || 1}`}
                            </span>
                            {art.wordCount !== undefined && (
                              <span className="text-[10px] font-medium text-zinc-400">
                                {art.wordCount} mots
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="text-xs text-zinc-800 whitespace-pre-line leading-relaxed font-serif bg-white p-3.5 rounded-xl border border-zinc-200/70 shadow-xs">
                          {art.text || art.rawText || 'Aucun texte.'}
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="py-12 text-center text-zinc-500 text-xs">
                      Aucun article spécifique segmenté. Consultez l'onglet "Code JSON Brut" pour voir les mots et pages brutes.
                    </div>
                  )}
                </div>
              ) : (
                /* Raw JSON Code View */
                <pre className="p-4 bg-zinc-950 text-emerald-400 rounded-2xl font-mono text-xs overflow-x-auto leading-relaxed max-h-[60vh]">
                  {JSON.stringify(inspectJsonData, null, 2)}
                </pre>
              )}
            </div>

            {/* Modal Footer */}
            <div className="p-4 bg-zinc-50 border-t border-zinc-200 flex items-center justify-end">
              <button
                type="button"
                onClick={() => setInspectDoc(null)}
                className="px-5 py-2 bg-zinc-200 hover:bg-zinc-300 text-zinc-800 font-bold text-xs rounded-xl transition-colors"
              >
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
